import type {ReactElement} from 'react';
import {useState} from 'react';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {SocialPostCaptionOverwriteEvent} from '@Components/social-media/social-media.types';
import {updateCommonCaption, updateHasCommonCaption, updatePublishingParamAllCaptions} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {useAppDispatch} from '@/hooks';
import type {CommonCaptionCheckboxProps} from './common-caption-checkbox.types';
import styles from './common-caption-checkbox.module.scss';

export function CommonCaptionCheckbox({accountIdsToPublishToCount, hasCommonCaption, textAreaRef}: CommonCaptionCheckboxProps): ReactElement | null {
  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  const dispatch = useAppDispatch();

  const onUseCaptionChange = (isChecked: boolean): void => {
    if (isChecked) {
      setIsShowingConfirmation(true);
    } else {
      dispatch(updateHasCommonCaption(false));
    }
  };

  const enableOverwriteCaptions = (): void => {
    const unifiedCaption = textAreaRef.current?.value;

    if (!hasCommonCaption) {
      dispatch(updateHasCommonCaption(true));
    }

    if (unifiedCaption) {
      dispatch(updateCommonCaption(unifiedCaption));
      dispatch(updatePublishingParamAllCaptions({caption: unifiedCaption, isMultiCharacterChange: true}));
    }
  };

  const onConfirmOverwriteCaptions = (): void => {
    window.dispatchEvent(new CustomEvent(SocialPostCaptionOverwriteEvent));
    dispatch(updateHasCommonCaption(true));
    enableOverwriteCaptions();
    setIsShowingConfirmation(false);
  };

  const onCancelOverwriteCaptions = (): void => {
    setIsShowingConfirmation(false);
  };

  const renderConfirmation = (): ReactElement | null => {
    if (!isShowingConfirmation) {
      return null;
    }

    return (
      <div className={`flex-v-row ${styles.captionConfirmationBox}`}>
        <div className="flex-row-align-center spacing-m-b-3">
          <Icon icon="icon-info" className={`color-warning-dark ${styles.infoIconSpacer}`} type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
          <Text val={window.i18next.t('pmwjs_are_you_sure_use_caption')} size={TextSize.XSMALL} className={styles.captionConfirmationText} />
        </div>
        <div className="flex-h-row flex-content-end">
          <Button type={Type.SECONDARY} size={Size.SMALL} onClick={onCancelOverwriteCaptions} text={window.i18next.t('pmwjs_cancel')} customClasses="spacing-m-r-2" />
          <Button type={Type.SECONDARY} size={Size.SMALL} onClick={onConfirmOverwriteCaptions} text={window.i18next.t('pmwjs_yes')} />
        </div>
      </div>
    );
  };

  if (accountIdsToPublishToCount <= 1) {
    return null;
  }

  return (
    <div className="_position-relative">
      <LabelledCheckbox className={styles.captionLabel} onChange={onUseCaptionChange} isChecked={hasCommonCaption} label={window.i18next.t('pmwjs_use_caption_all_posts')} />
      {renderConfirmation()}
    </div>
  );
}
